import { useState } from 'react';
import { FileUpload } from '../FileUpload';
import SftpImport from '../SftpImport';
import { IconUpload, IconServer, IconDatabase } from '../UploadIcons';
import S3Import from '../S3Import';
import DataSourceImport, {
  DATA_SOURCES,
  DATA_SOURCE_IDS,
  type DataSourceId,
} from '../ModelImport';
import { SET_ON_CONTINUE } from '../../../reducers/actionReducer';

interface FileImportSelectorProps {
  dispatchAction: (action: any) => void;
  /**
   * ATAD-136: offer the "ZIP code file" type. Only the Audience Builder and
   * My Work pass this — Enhancements and Insights need matched ID files.
   */
  allowZipFiles?: boolean;
  /**
   * ATAD-146: offer the Alliant data sources (CMDA model scores, First Party,
   * Syndicated Alliant) — importing a file from their shared folders. Pass
   * canUseModelFiles(user): the server refuses anyone without the
   * model-files-user role (admins pass).
   */
  allowDataSources?: boolean;
}

type ImportMethod = 'local' | 'sftp' | 's3';

/**
 * What the rail has selected: an Alliant data source, or one of the "Other
 * files" a user uploads themselves.
 */
type Selection = DataSourceId | 'regular' | 'zip';

const isDataSource = (selection: Selection): selection is DataSourceId =>
  selection in DATA_SOURCES;

// Flag to easily enable/disable file uploads - set to false to disable
const FILE_UPLOADS_ENABLED = true;

const ZIP_REMOTE_IMPORT_TOOLTIP = 'ZIP code files can only be uploaded from your computer';

const METHODS: { method: ImportMethod; label: string; Icon: typeof IconUpload }[] = [
  { method: 'local', label: 'Computer', Icon: IconUpload },
  { method: 'sftp', label: 'SFTP', Icon: IconServer },
  { method: 's3', label: 'S3', Icon: IconDatabase },
];

const fileIconProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const OTHER_FILES: { selection: 'regular' | 'zip'; label: string; icon: JSX.Element }[] = [
  {
    selection: 'regular',
    label: 'Regular file',
    icon: (
      <svg {...fileIconProps}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
      </svg>
    ),
  },
  {
    selection: 'zip',
    label: 'ZIP code file',
    icon: (
      <svg {...fileIconProps}>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
];

const RAIL_HEADING = 'text-xs font-semibold tracking-wider uppercase text-gray-600 px-2';

/**
 * Regular and ZIP code files: how to add one (computer, SFTP or S3; ZIP
 * files only from the computer), then the matching upload form.
 */
function UploadPane({
  dispatchAction,
  isZip,
  zipUploadAvailable,
  selectedMethod,
  onSelectMethod,
}: {
  dispatchAction: (action: any) => void;
  isZip: boolean;
  zipUploadAvailable: boolean;
  selectedMethod: ImportMethod;
  onSelectMethod: (method: ImportMethod) => void;
}) {
  return (
    <div className="w-full flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-gray-800">
          {isZip ? 'Upload a ZIP code file' : 'Upload a regular file'}
        </h3>
        <p className="text-sm text-gray-600">
          {isZip
            ? 'A list of ZIP codes to use as a filter. CSV, one ZIP code per row, up to 10 MB.'
            : 'Audience IDs matched to records. CSV with aiq_hhid and aiq_indid columns.'}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <span id="upload-method-label" className="text-sm font-semibold text-gray-800">
          Upload method
        </span>
        <div
          role="radiogroup"
          aria-labelledby="upload-method-label"
          className="grid grid-cols-3 gap-2"
        >
          {METHODS.map(({ method, label, Icon }) => {
            const disabled = isZip && method !== 'local';
            const checked = selectedMethod === method;
            return (
              <button
                key={method}
                type="button"
                role="radio"
                aria-checked={checked}
                onClick={() => onSelectMethod(method)}
                disabled={disabled}
                title={disabled ? ZIP_REMOTE_IMPORT_TOOLTIP : undefined}
                className={`flex items-center justify-center gap-2 min-h-[48px] rounded-lg text-sm transition-colors ${
                  disabled
                    ? 'border border-gray-200 bg-white text-gray-700 font-medium opacity-[.45] cursor-not-allowed'
                    : checked
                      ? 'border-2 border-primary bg-[#f0fdfa] text-primary-dark font-semibold'
                      : 'border border-gray-200 bg-white text-gray-700 font-medium hover:border-gray-300'
                }`}
              >
                <Icon className="w-[18px] h-[18px]" />
                {label}
              </button>
            );
          })}
        </div>
        {isZip && (
          <span className="text-[13px] text-gray-600">
            ZIP code files can only be uploaded from your computer.
          </span>
        )}
      </div>

      <div className="flex-1">
        {selectedMethod === 'local' ? (
          <FileUpload
            // Remount on type change so validation state can't carry over.
            key={isZip ? 'zip' : 'id'}
            dispatchAction={dispatchAction}
            kind={isZip ? 'zip' : 'id'}
            zipUploadAvailable={zipUploadAvailable}
          />
        ) : selectedMethod === 'sftp' ? (
          <SftpImport dispatchAction={dispatchAction} />
        ) : selectedMethod === 's3' ? (
          <S3Import dispatchAction={dispatchAction} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * The Upload File modal's body. ATAD-146: a two-pane layout (render it in an
 * ActionModal with layout 'panes') — a rail of Alliant data sources with the
 * user's own "Other files" below, and the selected one's pane on the right.
 * Where there is nothing to choose (Enhancements, Insights) it is just the
 * regular upload, without a rail.
 */
export function FileImportSelector({
  dispatchAction,
  allowZipFiles = false,
  allowDataSources = false,
}: FileImportSelectorProps) {
  const dataSources = allowDataSources ? DATA_SOURCE_IDS : [];
  const showRail = dataSources.length > 0 || allowZipFiles;
  const [selection, setSelection] = useState<Selection>(dataSources[0] ?? 'regular');
  const [selectedMethod, setSelectedMethod] = useState<ImportMethod>('local');
  const isZip = allowZipFiles && selection === 'zip';

  function select(next: Selection) {
    if (next === selection) return;
    setSelection(next);
    // ZIP files are computer uploads only.
    if (next === 'zip') setSelectedMethod('local');
    // A file validated as one type must not upload as the other: clear the
    // modal's Continue until the remounted body wires it up again.
    // ATAD-146: a SET_ON_CONTINUE without a continueLabel also drops a data
    // source's "Import" label, so the button reads Continue again.
    dispatchAction({ type: SET_ON_CONTINUE, payload: { onContinue: null } });
  }

  if (!FILE_UPLOADS_ENABLED) {
    return (
      <div className="w-full flex flex-col">
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-2">Choose your upload method</h3>
          <p className="text-sm text-gray-600 mb-6">Select how you want to add your file</p>
          <div className="grid grid-cols-3 gap-4">
            {(['local', 'sftp', 's3'] as const).map((method) => (
              <button
                key={method}
                type="button"
                disabled
                className="flex flex-col items-center justify-center p-6 border-2 rounded-lg text-center transition-all duration-150 border-gray-200 bg-white opacity-50 cursor-not-allowed"
              >
                <div className="w-16 h-16 rounded-full p-4 mb-3 flex items-center justify-center bg-gray-200">
                  {method === 'local' && <IconUpload />}
                  {method === 'sftp' && <IconServer />}
                  {method === 's3' && <IconDatabase />}
                </div>
                <span className="font-semibold text-gray-800">
                  {method === 'local' && 'Computer'}
                  {method === 'sftp' && 'SFTP'}
                  {method === 's3' && 'S3'}
                </span>
                <span className="text-sm text-gray-500 mt-1">
                  {method === 'local' && 'Local file'}
                  {method === 'sftp' && 'Server file'}
                  {method === 's3' && 'Cloud storage'}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg border border-gray-300">
          <p className="text-gray-700 font-medium">File Uploads are currently disabled. Please contact your System Administrator.</p>
        </div>
      </div>
    );
  }

  const pane = isDataSource(selection) ? (
    <DataSourceImport
      // Remount per source: its selection and search belong to one folder.
      key={selection}
      dispatchAction={dispatchAction}
      source={selection}
    />
  ) : (
    <UploadPane
      dispatchAction={dispatchAction}
      isZip={isZip}
      zipUploadAvailable={allowZipFiles}
      selectedMethod={selectedMethod}
      onSelectMethod={setSelectedMethod}
    />
  );

  if (!showRail) return pane;

  const otherFiles = OTHER_FILES.filter(
    ({ selection: kind }) => kind === 'regular' || allowZipFiles
  );

  return (
    <div className="flex w-full h-full min-h-0">
      <nav
        aria-label="What to add"
        className="w-72 flex-shrink-0 bg-gray-50 border-r border-gray-200 px-4 py-5 flex flex-col gap-1.5 overflow-y-auto"
      >
        {dataSources.length > 0 && (
          <>
            <span className={`${RAIL_HEADING} pb-2`}>Data sources</span>
            {dataSources.map((id) => {
              const { label, tagline, Icon } = DATA_SOURCES[id];
              const on = selection === id;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => select(id)}
                  className={`flex items-center gap-3 w-full p-3 min-h-[64px] rounded-[10px] text-left border transition-colors ${
                    on
                      ? 'border-primary bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]'
                      : 'border-transparent hover:bg-gray-100'
                  }`}
                >
                  <span
                    className={`w-10 h-10 flex-shrink-0 rounded-[10px] flex items-center justify-center ${
                      on ? 'bg-primary-dark text-white' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    <Icon />
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-[15px] font-semibold text-gray-800">{label}</span>
                    <span className="text-[13px] text-gray-600">{tagline}</span>
                  </span>
                </button>
              );
            })}
            <div className="flex-1" />
          </>
        )}

        <div
          className={`flex flex-col gap-0.5 ${
            dataSources.length > 0 ? 'border-t border-gray-200 pt-3.5' : ''
          }`}
        >
          <span className={`${RAIL_HEADING} pb-1.5`}>
            {dataSources.length > 0 ? 'Other files' : 'Files'}
          </span>
          {otherFiles.map(({ selection: kind, label, icon }) => {
            const on = selection === kind;
            return (
              <button
                key={kind}
                type="button"
                aria-pressed={on}
                onClick={() => select(kind)}
                className={`flex items-center gap-2.5 w-full min-h-[44px] px-2.5 rounded-lg text-left text-sm transition-colors ${
                  on
                    ? 'font-semibold bg-[#e6f4f2] text-primary-dark'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {icon}
                {label}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="flex-1 min-w-0 overflow-y-auto p-6">{pane}</div>
    </div>
  );
}

export default FileImportSelector;
